import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number; // ms
  actionLabel?: string;
  actionHref?: string;
}

interface ToastContextType {
  addToast: (toast: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: Toast = { id, ...toast };
    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after duration or 4s
    const ttl = toast.duration ?? 4000;
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ttl);
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-50 space-y-2 w-[280px]">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-start p-4 rounded-lg shadow-lg text-sm text-white animate-slide-in-right ring-1 ring-black/5  ${toast.type === 'success' ? 'bg-green-600' : toast.type === 'error' ? 'bg-red-600' : toast.type === 'warning' ? 'bg-yellow-600' : 'bg-slate-700'}`}
          >
            <div className="flex flex-col space-y-1">
              <span>{toast.message}</span>
              {toast.actionHref && toast.actionLabel && (
                <a href={toast.actionHref} className="underline text-white mt-1 text-xs" >{toast.actionLabel}</a>
              )}
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

/* Tailwind animation */
// Add the following to global css if not present:
// .animate-slide-in-right { @apply transform transition duration-300 ease-out translate-x-full opacity-0; }
// .animate-slide-in-right { animation: slide-in-right 0.3s forwards; }
// @keyframes slide-in-right { from { transform: translateX(100%); opacity:0 } to { transform: translateX(0); opacity:1 } } 